import { useRef } from "react";
import { ChannelBox } from "./ChannelBox.js";
import { usePlayerStore } from "../player/playerStore.js";
import { useEpgSettings } from "./settingsStore.js";
import type { Channel } from "@hybrid-iptv/types";

interface CategoryOption {
  id: string;
  name: string;
  count: number;
}

interface EpgToolbarProps {
  categories: CategoryOption[];
  activeCategoryId: string | null;
  onSelectCategory: (id: string | null) => void;
  channels: Channel[];
  onZapTo: (channel: Channel) => void;
}

/**
 * Guide toolbar: category and channel, and nothing else.
 *
 * It used to carry Time, Layout, Opacity, Size, Signal and Refresh as well,
 * which is six controls to step past with a d-pad before reaching a channel.
 * Those are set once and then left alone, so they live in Settings → Guide now
 * (owner 2026-09-17, matching how Sparkle arranges the same options). What is
 * left is the two things you actually use while watching: which category you
 * are in, and jumping to a channel by number.
 */
export function EpgToolbar({
  categories,
  activeCategoryId,
  onSelectCategory,
  channels,
  onZapTo,
}: EpgToolbarProps) {
  // When the dropdown was last opened on purpose (OK, a click or a tap). The
  // category once changed with nobody choosing it (projector, 2026-09-27, to
  // "Usa Movies"); every change is now logged with whether it followed one, so
  // a stray change says where it came from in the system log.
  const openedAt = useRef(0);
  const arm = () => {
    openedAt.current = Date.now();
  };
  // Full screen from the top of the guide (owner, 2026-10-06): in Overlay the
  // picture is the guide's backdrop, with no window of its own to press OK on
  // (Traditional's preview window is the stop for this there). Shown only
  // while something plays, so a press never lands on a dead button.
  const overlay = useEpgSettings((s) => s.layout) === "overlay";
  const playing = usePlayerStore((s) => s.source !== null);
  return (
    <div className="epg-toolbar">
      <div className="epg-toolbar-controls">
        <label className="epg-toolbar-label epg-toolbar-label--category">
          <span className="epg-toolbar-label__text">Category</span>
          <select
            value={activeCategoryId ?? ""}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") arm();
            }}
            onPointerDown={arm}
            onClick={arm}
            onChange={(e) => {
              const next = e.target.value === "" ? null : e.target.value;
              const sinceOpen = Date.now() - openedAt.current;
              console.info(`[guide] category -> ${next ?? "all"} (${sinceOpen < 120_000 ? `chosen ${Math.round(sinceOpen / 1000)}s after opening the list` : "WITHOUT the list being opened"}; focus: ${document.activeElement?.className ?? "?"})`);
              onSelectCategory(next);
            }}
            className="epg-toolbar-select epg-toolbar-select--category"
          >
            <option value="">All categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name} ({cat.count})
              </option>
            ))}
          </select>
        </label>

        {overlay && playing && (
          <button
            type="button"
            className="epg-toolbar-fullscreen"
            onClick={(e) => {
              // Back returns focus here (PlayerSurface's full-screen exit).
              e.currentTarget.setAttribute("data-fs-opener", "");
              e.currentTarget.blur();
              usePlayerStore.getState().requestFullscreen();
            }}
          >
            ⛶ Full screen
          </button>
        )}

        <ChannelBox channels={channels} onZapTo={onZapTo} />
      </div>
    </div>
  );
}
